import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { CheckCircle, ExternalLink, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import ParticipantList from "@/components/ParticipantList";
import ExperimentResultsList from "@/components/ExperimentResultsList";
import AggregateExperimentResults from "@/components/AggregateExperimentResults";
import { useExperimentDetails } from "@/hooks/useExperiments";
import { useExperimentParticipants } from "@/hooks/useExperimentParticipants";
import { useExperimentResults } from "@/hooks/useExperimentResults";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { formatUrlForDisplay } from "@/lib/utils";
import ReactMarkdown from "react-markdown";
import MarkdownEditorModal from "@/components/MarkdownEditorModal";
import JsonEditorModal from "@/components/JsonEditorModal";
import ExperimentDetailsEditorModal from "@/components/ExperimentDetailsEditorModal";
import ExperimentProtocolPreview from "@/components/ExperimentProtocolPreview";
import { useQueryClient } from "@tanstack/react-query";
import LinkedProductsList from "@/components/LinkedProductsList";
import QrCodeModal from "@/components/QrCodeModal";
import ExperimentStatsCards from "@/components/ExperimentStatsCards";
const defaultProtocol = {
  "days": [{
    "title": "Day 1",
    "description": "Day description",
    "actions": [{
      "meta": {
        "duration": "08:00:00",
        "model_time": null
      },
      "type": "fasting",
      "title": "Fast for 8 hours",
      "description": "Fast for 8 hours to improve your health"
    }, {
      "meta": {
        "model_time": "08:00:00",
        "product_ids": ["abc"]
      },
      "type": "product",
      "title": "Consume the product",
      "description": "Step description"
    }, {
      "meta": {
        "duration": "02:00:00",
        "model_time": "10:00:00"
      },
      "type": "satiety",
      "title": "Log the satiety",
      "description": "Wait for 2 hours and then log your hunger level"
    }]
  }]
};
const ExperimentDetail: React.FC = () => {
  const {
    experimentId
  } = useParams();
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const {
    organization
  } = useOrganization();
  const queryClient = useQueryClient();
  const {
    data: experiment,
    isLoading,
    error
  } = useExperimentDetails(experimentId);
  const {
    data: participants = [],
    isLoading: isLoadingParticipants
  } = useExperimentParticipants(experimentId);
  const {
    data: experimentResults,
    isLoading: isLoadingResults
  } = useExperimentResults(experimentId || '');
  const refreshExperiment = () => {
    queryClient.invalidateQueries({
      queryKey: ['experiment', experimentId, organization?.id]
    });
    queryClient.invalidateQueries({
      queryKey: ['experiment-results', experimentId]
    });
  };
  const completeExperiment = () => {
    if (!experiment) return;
    toast.success("Experiment marked as completed");
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500 hover:bg-green-600">Active</Badge>;
      case "completed":
        return <Badge className="bg-blue-500 hover:bg-blue-600">Completed</Badge>;
      case "draft":
        return <Badge className="bg-yellow-500 hover:bg-yellow-600">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };
  if (isLoading) {
    return <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-4 w-96" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>;
  }
  if (error) {
    return <div className="space-y-6">
        <div className="text-center py-6">
          <p className="text-destructive">
            Error loading experiment: {error.message}
          </p>
          <Button onClick={() => navigate("/dashboard/experiments")} className="mt-4">
            Back to Experiments
          </Button>
        </div>
      </div>;
  }
  if (!experiment) {
    return <div className="space-y-6">
        <div className="text-center py-6">
          <p className="text-muted-foreground">Experiment not found</p>
          <Button onClick={() => navigate("/dashboard/experiments")} className="mt-4">
            Back to Experiments
          </Button>
        </div>
      </div>;
  }
  const isAdmin = user?.role === "admin";
  return <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{experiment.name}</h1>
            {isAdmin && getStatusBadge(experiment.status)}
          </div>
          <p className="text-muted-foreground">
            Experiment details and participant information
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/dashboard/experiments")}>
            Back to Experiments
          </Button>
          <Button variant="outline" size="icon" onClick={refreshExperiment} title="Refresh experiment data">
            <RefreshCw className="h-4 w-4" />
          </Button>
          {experiment.status === "active" && isAdmin && <Button onClick={completeExperiment}>
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark as Complete
            </Button>}
        </div>
      </div>

      <ExperimentStatsCards experimentId={experimentId || ""} />
      <Separator />

      {/* Experiment Results Section */}
      <div className="space-y-6">
        {/* Aggregate Experiment Results */}
        {isLoadingResults ? (
          <Card>
            <CardHeader>
              <CardTitle>Aggregate Experiment Results</CardTitle>
              <CardDescription>Loading aggregate results...</CardDescription>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-80 w-full" />
            </CardContent>
          </Card>
        ) : experimentResults && experimentResults.meal_impacts.length > 0 ? (
          <AggregateExperimentResults 
            results={experimentResults.meal_impacts} 
            experimentId={experimentId} 
          />
        ) : null}

        {/* Individual Experiment Results */}
        {isLoadingResults ? <Card>
            <CardHeader>
              <CardTitle>Individual Experiment Results</CardTitle>
              <CardDescription>
                Loading experiment results...
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Skeleton className="h-32 w-full" />
            </CardContent>
          </Card> : experimentResults && experimentResults.meal_impacts.length > 0 ? <ExperimentResultsList results={experimentResults.meal_impacts} experimentId={experimentId || ''} experimentName={experiment.name} linkedProducts={experiment.products?.map(product => ({
        id: product.fdc_id.toString(),
        name: product.title || product.description,
        image_url: product.image_url
      }))} /> : <Card>
            <CardHeader>
              <CardTitle>Individual Experiment Results</CardTitle>
              <CardDescription>
                Logged meal impacts and glucose responses
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-6 text-muted-foreground">
                No experiment results available yet.
              </div>
            </CardContent>
          </Card>}
      </div>

      <Separator />

      {/* Experiment Participants Section - Admin Only */}
      {isAdmin && (
        <div className="space-y-6">
          {isLoadingParticipants ? <Card>
              <CardHeader>
                <CardTitle>Experiment Participants</CardTitle>
                <CardDescription>
                  Loading participant information...
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Skeleton className="h-32 w-full" />
              </CardContent>
            </Card> : participants.length > 0 ? <ParticipantList participants={participants} showPremiumRows={false} title="Experiment Participants" /> : <Card>
              <CardHeader>
                <CardTitle>Experiment Participants</CardTitle>
                <CardDescription>
                  People who have participated in this experiment
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-center py-6 text-muted-foreground">
                  No participants have joined this experiment yet.
                </div>
              </CardContent>
            </Card>}
        </div>
      )}

      <Separator />

      {/* Overview and Protocol moved below */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <div>
                <CardTitle>Experiment Overview</CardTitle>
                <CardDescription>
                  Basic details about this experiment
                </CardDescription>
              </div>
              {isAdmin && (
                <ExperimentDetailsEditorModal
                  experiment={{
                    id: experiment.id,
                    name: experiment.name,
                    description: experiment.description,
                    status: experiment.status,
                    introductionUrl: experiment.introductionUrl,
                    qrUrl: experiment.qrUrl,
                    image: experiment.image,
                    landingPageUrl: experiment.landingPageUrl,
                    shopUrl: experiment.shopUrl,
                    brandProductUrl: experiment.brandProductUrl,
                    price: experiment.price,
                  }}
                  onSave={refreshExperiment}
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Experiment Image */}
            {experiment.image && (
              <div className="flex justify-center">
                <img
                  src={experiment.image}
                  alt={experiment.name}
                  className="w-full max-w-md object-cover rounded-lg border"
                />
              </div>
            )}
            {/* Experiment Description */}
            {experiment.description && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">
                  Description (short)
                </p>
                <div className="text-sm prose-sm max-w-none whitespace-pre-wrap">
                  {experiment.description}
                </div>
              </div>
            )}

            {/* Experiment Description as markdown */}

            <div>
              <div className="flex items-center mb-2">
                <p className="text-sm font-medium text-muted-foreground">Markdown Description</p>
              <MarkdownEditorModal
                initialMarkdown={experiment.descriptionMarkdown}
                title="Description"
                experimentId={experimentId}
                onSave={refreshExperiment}
              />
              </div>
              <div className="text-sm prose prose-sm max-w-none">
                <ReactMarkdown
                  components={{
                    img: ({ src, alt }) => (
                      <img
                        src={src}
                        alt={alt}
                        className="w-full max-w-sm h-auto object-cover rounded-lg border my-2"
                      />
                    ),
                    h1: ({ children }) => (
                      <h1 className="text-xl font-bold mb-2">{children}</h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-lg font-semibold mb-2">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-base font-medium mb-1">{children}</h3>
                    ),
                    p: ({ children }) => <p className="mb-2">{children}</p>,
                    ul: ({ children }) => (
                      <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>
                    ),
                    li: ({ children }) => <li className="text-sm">{children}</li>,
                    a: ({ href, children }) => (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        {children}
                      </a>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                    em: ({ children }) => <em className="italic">{children}</em>,
                    code: ({ children }) => (
                      <code className="bg-gray-100 px-1 py-0.5 rounded text-xs font-mono">
                        {children}
                      </code>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-gray-200 pl-4 italic my-2">
                        {children}
                      </blockquote>
                    ),
                  }}
                >
                  {experiment.descriptionMarkdown ?? "No markdown description added yet..."}
                </ReactMarkdown>
              </div>
            </div>

            {isAdmin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Status</p>
                  <p className="capitalize">{experiment.status}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Participants</p>
                  <p>
                    {experiment.participantsCount !== null &&
                    experiment.participantsCount !== undefined ? (
                      <span>{experiment.participantsCount}</span>
                    ) : (
                      <span className="text-muted-foreground">0</span>
                    )}
                  </p>
                </div>
                {experiment.price !== null && experiment.price !== undefined && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Price</p>
                    <p>${experiment.price} USD</p>
                  </div>
                )}
                {experiment.shortCode && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Short Code</p>
                    <p className="text-sm font-mono">{experiment.shortCode}</p>
                  </div>
                )}
                {experiment.shortCode && (
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Deep Link</p>
                    <div className="flex items-center gap-1">
                      <a
                        href={`https://api.tastermonial.com/app/start-experiment/?short-code=${experiment.shortCode}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        {`https://api.tastermonial.com/app/start-experiment/?short-code=${experiment.shortCode}`} <ExternalLink className="h-3 w-3" />
                      </a>
                      <QrCodeModal
                        url={`https://api.tastermonial.com/app/start-experiment/?short-code=${experiment.shortCode}`}
                        title="Deep Link QR Code"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Product URLs and Properties */}
            <div className="space-y-3">
              {experiment.introductionUrl && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Introduction URL</p>
                  <a
                    href={experiment.introductionUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {formatUrlForDisplay(experiment.introductionUrl)} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {isAdmin && experiment.shopUrl && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Shop URL</p>
                  <a
                    href={experiment.shopUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {formatUrlForDisplay(experiment.shopUrl)} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {isAdmin && experiment.landingPageUrl && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Landing Page URL</p>
                  <a
                    href={experiment.landingPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {formatUrlForDisplay(experiment.landingPageUrl)} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {isAdmin && experiment.brandProductUrl && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground">Brand Product URL</p>
                  <a
                    href={experiment.brandProductUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                  >
                    {formatUrlForDisplay(experiment.brandProductUrl)} <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}

              {/* Admin-only fields */}
              {isAdmin && (
                <>
                  {experiment.qrUrl && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">QR URL</p>
                      <a
                        href={experiment.qrUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        {formatUrlForDisplay(experiment.qrUrl)} <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}

                  {experiment.twilioFlowSid && (
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Twilio Flow SID</p>
                      <p className="text-sm font-mono">{experiment.twilioFlowSid}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center">
              <CardTitle>Experiment Protocol</CardTitle>
              {isAdmin && (
                <JsonEditorModal
                  initialJson={experiment.experimentProtocol ?? defaultProtocol}
                  title="Experiment Protocol"
                  experimentId={experimentId}
                  onSave={refreshExperiment}
                  renderPreview={(jsonData) => (
                    <ExperimentProtocolPreview
                      protocolData={(jsonData ?? defaultProtocol) as any}
                      className="p-3"
                    />
                  )}
                />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {experiment.experimentProtocol ? (
              <ExperimentProtocolPreview protocolData={experiment.experimentProtocol} />
            ) : (
              <div className="text-center py-6 text-muted-foreground">No protocol defined yet.</div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Linked Products Section */}
      <div className="space-y-6">
        <LinkedProductsList products={experiment.products || []} />
      </div>
    </div>;
};
export default ExperimentDetail;